/**
 * The building's own fabric, for the props that are made of it.
 *
 * A half-wall is not a prop shaped like a wall; it is a piece of the same
 * building, put somewhere with no purpose and then left. If it arrives in plain
 * grey it reads as a crate. The unease depends entirely on the player
 * recognising the room's own wallpaper and the room's own skirting board on
 * something that has no business standing in the middle of the floor — and then
 * on the ends, where the paper stops and the plain core shows, because that is
 * the part a builder would never have left like that.
 *
 * `render/materials.ts` already makes exactly these surfaces, but a prop view is
 * only handed a `LevelSpec` (see `CreatePropView` in `api.ts`), so it cannot
 * reach the renderer's `MaterialSet`. Rather than generate a second full
 * `TextureSet` — the expensive one, with the floor's normal map in it — this
 * draws only the two maps architecture needs, at a quarter of the area, from the
 * same palette entries and at the same metres-per-tile. The blotches land
 * somewhere different from the room's; the hue, the pitch, the stripe and the
 * skirting do not, and at this light level that is what the eye is matching.
 *
 * Everything here is shared: one bundle per level, reference counted across
 * every architectural kind that asks for it.
 */

import * as THREE from 'three';
import { clamp01, hash2, smoothstep, TAU } from '../core/math';
import { hashSeed, makeRNG } from '../core/rng';
import type { LevelSpec, RNG } from '../core/types';
import { SpecKit, type Disposable } from './propkit';

/* ------------------------------------------------------------------ *
 * Dimensions shared with the level mesh
 * ------------------------------------------------------------------ */

/**
 * Skirting board height and how far it stands proud of the wall face. These
 * must match `TRIM_HEIGHT` / `TRIM_PROUD` in `meshbuilder.ts`: a half-wall whose
 * skirting is a different height from the room's is the one detail that gives
 * away that it was dropped in rather than built.
 */
export const TRIM_HEIGHT = 0.17;
export const TRIM_PROUD = 0.03;

/**
 * Metres of wall per repeat of the wall map. `meshbuilder.ts` divides world
 * coordinates by `REPEAT.wall` (1.4) and `materials.ts` sets the texture repeat
 * to `1 / TILE_METRES.wall` (0.5), so the room's paper tiles every 2.8 m.
 */
export const PAPER_METRES = 2.8;

/** The trim map is the bottom of a wall, clamped and squeezed into the band. */
const SKIRT_METRES = 1.0;

/**
 * Metres a wall-mounted prop is set off the wall by the placer (`WALL_BACK` in
 * `world/gen/props.ts`). Anything that has to look *set into* the wall — a door,
 * a window — cancels it with a negative `Anchor.out` and keeps a few millimetres
 * so the casing does not z-fight the wallpaper.
 */
export const WALL_INSET = -0.075;

/* ------------------------------------------------------------------ *
 * Texture generation
 * ------------------------------------------------------------------ */

/**
 * Half the renderer's 512. Architecture is seen at a metre or two and never
 * fills the screen the way a corridor wall does, and this is generated on top of
 * a level load that is already building seven maps.
 */
const TEX = 256;

interface RGB {
  r: number;
  g: number;
  b: number;
}

function rgb(hex: number): RGB {
  return { r: (hex >> 16) & 255, g: (hex >> 8) & 255, b: hex & 255 };
}

function shade(c: RGB, mul: number): RGB {
  return { r: Math.min(255, c.r * mul), g: Math.min(255, c.g * mul), b: Math.min(255, c.b * mul) };
}

function mix(a: RGB, b: RGB, t: number): RGB {
  return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t };
}

/**
 * Periodic fractal value noise over the whole canvas, `TEX * TEX` floats in
 * 0..1. The lattice wraps, so the field tiles — the same requirement, and the
 * same trick, as `textures.ts`, written small because nothing here differentiates
 * the result into a normal map.
 */
function fbm(cells: number, octaves: number, seed: number): Float32Array {
  const out = new Float32Array(TEX * TEX);
  let norm = 0;
  for (let o = 0, a = 1; o < octaves; o++, a *= 0.5) norm += a;

  let amp = 1 / norm;
  let n = cells;
  for (let o = 0; o < octaves; o++) {
    const i0 = new Int32Array(TEX);
    const i1 = new Int32Array(TEX);
    const f = new Float32Array(TEX);
    const s = n / TEX;
    for (let i = 0; i < TEX; i++) {
      const p = i * s;
      const c = Math.floor(p);
      const t = p - c;
      i0[i] = ((c % n) + n) % n;
      i1[i] = (i0[i]! + 1) % n;
      f[i] = t * t * t * (t * (t * 6 - 15) + 10);
    }
    for (let y = 0; y < TEX; y++) {
      const ya = i0[y]!;
      const yb = i1[y]!;
      const v = f[y]!;
      const row = y * TEX;
      for (let x = 0; x < TEX; x++) {
        const xa = i0[x]!;
        const xb = i1[x]!;
        const u = f[x]!;
        const t0 = hash2(xa, ya, seed + o * 7919);
        const t1 = hash2(xb, ya, seed + o * 7919);
        const b0 = hash2(xa, yb, seed + o * 7919);
        const b1 = hash2(xb, yb, seed + o * 7919);
        const top = t0 + (t1 - t0) * u;
        const bot = b0 + (b1 - b0) * u;
        out[row + x] += amp * (top + (bot - top) * v);
      }
    }
    amp *= 0.5;
    n *= 2;
  }
  return out;
}

function canvas2d(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = TEX;
  return { canvas, ctx: canvas.getContext('2d')! };
}

/**
 * The wall surface of one level, in the same four flavours `textures.ts`
 * switches between: papered, poured, tiled, panelled. Everything shares the same
 * mottled base and differs only in the marks laid over it, which is what makes
 * this a fortieth of the size of the real generator and still recognisably the
 * same wall.
 */
function wallCanvas(spec: LevelSpec, rng: RNG): HTMLCanvasElement {
  const pal = spec.palette;
  const { canvas, ctx } = canvas2d();
  const img = ctx.createImageData(TEX, TEX);
  const px = img.data;

  const base = rgb(pal.wall);
  const stain = shade(mix(base, rgb(pal.wallTrim), 0.55), 0.72);
  const mottle = fbm(6, 3, rng.int(0, 0xffff));
  const damp = fbm(3, 3, rng.int(0, 0xffff));
  const tooth = rng.int(0, 0xffff);

  const kind = spec.generator;
  // Architectural props carry the building's own wall surface, so this has to
  // agree with `wallCanvasFor` in textures.ts or a stub wall is made of
  // something the wall it grew out of is not. Level 3 gets `panelled` off:
  // brick has no seams at this scale and reads as plain rough render, which is
  // much closer than a metal panel joint.
  const papered = kind === 'lobby' || kind === 'hub' || kind === 'offices' || kind === 'hotel';
  const tiled = kind === 'poolrooms';
  const panelled = kind === 'pipes';
  // The paper's printed banding, at the pitch the room uses: 26 stripes over
  // the 2 m authored tile.
  const stripeStep = (26 * TAU) / TEX;
  const dropPx = TEX / 4;
  // Poolroom tile module and the pipe level's panel seams.
  const tilePx = TEX / 8;
  const panelPx = TEX / 3;

  for (let y = 0; y < TEX; y++) {
    const row = y * TEX;
    for (let x = 0; x < TEX; x++) {
      const idx = row + x;
      let v = 1 + (mottle[idx]! - 0.5) * (tiled ? 0.07 : 0.15);
      if (papered) {
        v += Math.sin(x * stripeStep) * 0.02;
        // The seam between paper drops: the only hard vertical line on it.
        const seam = Math.abs(((x + dropPx * 0.5) % dropPx) - dropPx * 0.5);
        if (seam < 1) v *= 0.962;
      }
      if (panelled) {
        const sx = Math.abs(((x + panelPx * 0.5) % panelPx) - panelPx * 0.5);
        const sy = Math.abs(((y + panelPx * 0.5) % panelPx) - panelPx * 0.5);
        if (sx < 1.2 || sy < 1.2) v *= 0.8;
      }
      v *= 0.986 + hash2(x, y, tooth) * 0.028;

      let c = shade(base, v);
      if (tiled) {
        const gx = Math.min(x % tilePx, tilePx - (x % tilePx));
        const gy = Math.min(y % tilePx, tilePx - (y % tilePx));
        if (gx < 1.4 || gy < 1.4) c = mix(c, rgb(pal.wallTrim), 0.55);
      } else {
        // Damp is a minority of the wall: patches with a darker tide line where
        // each dried out. Any more and it reads as camouflage.
        const d = damp[idx]!;
        const wet = clamp01(smoothstep(0.63, 0.8, d) * 0.42 + Math.exp(-Math.abs(d - 0.63) * 44) * 0.2);
        c = mix(c, stain, wet);
      }

      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

/**
 * The bottom of a wall: skirting at the floor with rising damp above it. Canvas
 * row 0 is the top, because three flips the upload and the material clamps V —
 * so the last row is the floor, exactly as in `textures.ts`.
 */
function trimCanvas(spec: LevelSpec, rng: RNG): HTMLCanvasElement {
  const pal = spec.palette;
  const { canvas, ctx } = canvas2d();
  const img = ctx.createImageData(TEX, TEX);
  const px = img.data;

  const tiled = spec.generator === 'poolrooms';
  const wall = rgb(pal.wall);
  const board = shade(rgb(pal.wallTrim), tiled ? 0.86 : 0.78);
  const damp = shade(mix(rgb(pal.wall), rgb(pal.carpetStain), 0.6), 0.8);

  const boardTop = TEX * (tiled ? 0.24 : 0.13);
  const mottle = fbm(6, 3, rng.int(0, 0xffff));
  const edge = fbm(9, 2, rng.int(0, 0xffff));
  const tooth = rng.int(0, 0xffff);

  for (let y = 0; y < TEX; y++) {
    const up = TEX - 1 - y;
    const row = y * TEX;
    for (let x = 0; x < TEX; x++) {
      const idx = row + x;
      let c: RGB;
      if (up < boardTop) {
        let v = 1 + (mottle[idx]! - 0.5) * 0.1;
        // A lit lip along the top of the board and a shadow where it meets the
        // floor. Without those two lines it is a stripe, not a moulding.
        if (boardTop - up < 1.5) v *= 1.16;
        if (up < 1) v *= 0.7;
        c = shade(board, v * (0.99 + hash2(x, y, tooth) * 0.02));
      } else {
        const h = (up - boardTop) / (TEX - boardTop);
        const v = (1 + (mottle[idx]! - 0.5) * 0.12) * (0.988 + hash2(x, y, tooth) * 0.024);
        const reach = 0.3 + (edge[idx]! - 0.5) * 0.26;
        const wet = clamp01(1 - h / Math.max(reach, 0.05));
        const tide = Math.exp(-Math.abs(h - reach) * 20) * 0.22;
        c = mix(shade(wall, v), damp, clamp01(wet * wet * 0.5 + tide));
      }
      const i = idx * 4;
      px[i] = c.r;
      px[i + 1] = c.g;
      px[i + 2] = c.b;
      px[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return canvas;
}

function toTexture(canvas: HTMLCanvasElement, clampV: boolean, uRepeat: number): THREE.Texture {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = clampV ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.minFilter = THREE.LinearMipmapLinearFilter;
  t.magFilter = THREE.LinearFilter;
  t.anisotropy = 8;
  t.repeat.set(uRepeat, clampV ? 1 : uRepeat);
  return t;
}

/* ------------------------------------------------------------------ *
 * The bundle
 * ------------------------------------------------------------------ */

/** How each level's surfaces answer a light. Mirrors `responseFor` in materials.ts. */
function roughnessFor(spec: LevelSpec): { rough: number; metal: number } {
  switch (spec.generator) {
    case 'warehouse':
    case 'lightsout':
    case 'city':
      return { rough: 0.94, metal: 0 };
    case 'pipes':
      return { rough: 0.62, metal: 0.3 };
    case 'station':
      return { rough: 0.93, metal: 0 };
    case 'poolrooms':
      return { rough: 0.24, metal: 0 };
    case 'offices':
    case 'hotel':
    default:
      return { rough: 0.97, metal: 0 };
  }
}

/** The building's materials, shared by every architectural prop on a level. */
export interface ArchKit extends Disposable {
  /** The level's own wall surface. UVs are metres; see `PAPER_METRES`. */
  paper: THREE.Material;
  /** Skirting board and rising damp. U is metres, V runs 0..1 over `TRIM_HEIGHT`. */
  skirt: THREE.Material;
  /**
   * A cut end: the plain core the paper never reached. Untextured on purpose —
   * a raw edge is the one surface in the building with nothing printed on it,
   * and the flatness is what makes a wall that simply stops look wrong.
   */
  plain: THREE.Material;
  /** Painted joinery: casings, linings, door leaves. Matches the level's doorframes. */
  joinery: THREE.Material;
  /** The same paint in shadow, for reveals and the edge of a leaf. */
  joineryDark: THREE.Material;
  /** Whatever is on the other side of an interior window. Nothing is. */
  dark: THREE.Material;
}

function buildArch(spec: LevelSpec): ArchKit {
  // Seeded off the level id like every other generated surface, so a level's
  // architecture looks the same every time it is entered.
  const rng = makeRNG(hashSeed(spec.id) ^ 0x5c4f);
  const res = roughnessFor(spec);

  const paperTex = toTexture(wallCanvas(spec, rng), false, 1 / PAPER_METRES);
  const skirtTex = toTexture(trimCanvas(spec, rng), true, 1 / SKIRT_METRES);

  const made: THREE.Material[] = [];
  const track = <T extends THREE.Material>(m: T): T => {
    made.push(m);
    return m;
  };

  // Plaster core: the wall's own colour drained most of the way to neutral and
  // taken down a stop, which is what an unpainted cut edge actually looks like.
  const core = new THREE.Color(spec.palette.wall);
  const grey = (core.r + core.g + core.b) / 3;
  core.lerp(new THREE.Color(grey, grey, grey), 0.72).multiplyScalar(0.86);

  // Kept in step with `materials.ts`'s doorframe by construction: painted the
  // colour of the wallpaper with a little more light on it, not stained timber.
  // A brown casing makes the place look like an office, which is the one thing
  // it must not look like.
  const joineryColor = new THREE.Color(spec.palette.wall).multiplyScalar(1.12);

  return {
    paper: track(new THREE.MeshStandardMaterial({ map: paperTex, roughness: res.rough, metalness: res.metal })),
    skirt: track(
      new THREE.MeshStandardMaterial({ map: skirtTex, roughness: Math.min(0.99, res.rough + 0.02), metalness: res.metal })
    ),
    plain: track(new THREE.MeshStandardMaterial({ color: core, roughness: 0.95, metalness: 0 })),
    joinery: track(new THREE.MeshStandardMaterial({ color: joineryColor, roughness: 0.72, metalness: 0 })),
    joineryDark: track(
      new THREE.MeshStandardMaterial({ color: joineryColor.clone().multiplyScalar(0.7), roughness: 0.8, metalness: 0 })
    ),
    dark: track(new THREE.MeshBasicMaterial({ color: 0x07080a })),
    dispose(): void {
      for (const m of made) m.dispose();
      made.length = 0;
      paperTex.dispose();
      skirtTex.dispose();
    },
  };
}

/**
 * One bundle per level, reference counted across the architectural kinds.
 *
 * A kind acquires it in its builder and hands `releaseArch(spec)` to
 * `makeShape`'s extras, so the textures live exactly as long as the last
 * half-wall, column or doorframe on the level.
 */
const ARCH = new SpecKit<ArchKit>(buildArch);

export function acquireArch(spec: LevelSpec): ArchKit {
  return ARCH.acquire(spec);
}

/** A disposable that gives one reference on the level's architecture back. */
export function releaseArch(spec: LevelSpec): Disposable {
  return {
    dispose(): void {
      ARCH.release(spec);
    },
  };
}

/* ------------------------------------------------------------------ *
 * Geometry
 * ------------------------------------------------------------------ */

type Facing = 'px' | 'nx' | 'py' | 'ny' | 'pz' | 'nz';

/**
 * One textured quad, `w` across by `h` up in its own plane, centred on
 * `(x, y, z)` and turned to face `dir`. UVs are given in texture space, so a
 * caller passes metres divided by the map's tile size and the pattern lands at
 * the same scale on every face of a prop.
 */
export function face(
  w: number,
  h: number,
  x: number,
  y: number,
  z: number,
  dir: Facing,
  u0: number,
  v0: number,
  u1: number,
  v1: number
): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, h);
  const uv = g.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, u0 + (u1 - u0) * uv.getX(i), v0 + (v1 - v0) * uv.getY(i));
  }
  uv.needsUpdate = true;
  if (dir === 'nz') g.rotateY(Math.PI);
  else if (dir === 'px') g.rotateY(Math.PI / 2);
  else if (dir === 'nx') g.rotateY(-Math.PI / 2);
  else if (dir === 'py') g.rotateX(-Math.PI / 2);
  else if (dir === 'ny') g.rotateX(Math.PI / 2);
  g.translate(x, y, z);
  return g;
}

/** The three material's worth of geometry a piece of wall comes in. */
export interface WallParts {
  paper: THREE.BufferGeometry[];
  skirt: THREE.BufferGeometry[];
  plain: THREE.BufferGeometry[];
}

export interface WallOptions {
  /** Emit a plain top face. False for anything that runs into the ceiling. */
  capped?: boolean;
  /**
   * Metres from the left end at which the paper gives out and the plain core
   * shows for the rest of the run. Somebody stopped decorating. Default: never.
   */
  paperEnds?: number;
  /** Same, for the skirting. A board that stops short of the end of the wall. */
  skirtEnds?: number;
  /** -1 or 1 to leave that face entirely bare. 0 papers both. */
  bareFace?: -1 | 0 | 1;
  /** Metres of UV phase, so two walls in one room are not the same wall twice. */
  uOffset?: number;
}

/**
 * A length of the building, standing on its own.
 *
 * Runs along local X and is centred on the origin in X and Z, because the
 * collision box in `PROP_DEFS` is: a wall drawn off-centre from the box that
 * blocks it is an invisible wall on one side and a ghost on the other.
 *
 * The ends are deliberately unfinished. Every other face is the room's own
 * surface, and then the wall stops and shows its core, which is the whole
 * effect: not a strange object, an ordinary one that was never completed.
 */
export function buildingWall(len: number, height: number, core: number, opts: WallOptions = {}): WallParts {
  const paper: THREE.BufferGeometry[] = [];
  const skirt: THREE.BufferGeometry[] = [];
  const plain: THREE.BufferGeometry[] = [];

  const hx = len / 2;
  const hz = core / 2;
  const sz = hz + TRIM_PROUD;
  const uo = opts.uOffset ?? 0;
  const paperEnd = Math.min(hx, opts.paperEnds !== undefined ? -hx + opts.paperEnds : hx);
  const skirtEnd = Math.min(hx, opts.skirtEnds !== undefined ? -hx + opts.skirtEnds : hx);
  const bare = opts.bareFace ?? 0;

  for (const side of [1, -1] as const) {
    const dir: Facing = side > 0 ? 'pz' : 'nz';
    const z = side * hz;
    // UVs are in metres; the materials carry the metres-per-tile as their
    // texture repeat, exactly as `materials.ts` does for the room's own walls.
    // Seen from the +Z side local +X runs to the right, from -Z it runs to the
    // left, so U is mirrored to keep the paper's drop seams vertical on both
    // faces instead of walking backwards across one of them.
    const u = (a: number): number => uo + (side > 0 ? a : -a);

    if (bare === side) {
      plain.push(face(len, height - TRIM_HEIGHT, 0, (height + TRIM_HEIGHT) / 2, z, dir, 0, 0, 1, 1));
    } else {
      const w = paperEnd + hx;
      if (w > 0.01) {
        paper.push(
          face(
            w,
            height - TRIM_HEIGHT,
            (-hx + paperEnd) / 2,
            (height + TRIM_HEIGHT) / 2,
            z,
            dir,
            u(-hx),
            TRIM_HEIGHT,
            u(paperEnd),
            height
          )
        );
      }
      if (paperEnd < hx - 0.01) {
        plain.push(face(hx - paperEnd, height - TRIM_HEIGHT, (paperEnd + hx) / 2, (height + TRIM_HEIGHT) / 2, z, dir, 0, 0, 1, 1));
      }
    }

    const sw = skirtEnd + hx;
    if (sw > 0.01) {
      const cx = (-hx + skirtEnd) / 2;
      skirt.push(face(sw, TRIM_HEIGHT, cx, TRIM_HEIGHT / 2, side * sz, dir, u(-hx), 0, u(skirtEnd), 1));
      // The lit top edge of the board, exactly as `ChunkBuilder.capStrip` emits
      // it. Two triangles, and without them the skirting is a printed stripe
      // rather than a moulding.
      skirt.push(face(sw, TRIM_PROUD, cx, TRIM_HEIGHT, side * (hz + TRIM_PROUD / 2), 'py', 0, 0, 1, 1));
    }
    if (skirtEnd < hx - 0.01) {
      // The board stopped before the wall did: its own cut return, and bare
      // wall down to the floor for the rest of the run.
      plain.push(face(TRIM_PROUD, TRIM_HEIGHT, skirtEnd, TRIM_HEIGHT / 2, side * (hz + TRIM_PROUD / 2), 'px', 0, 0, 1, 1));
      plain.push(face(hx - skirtEnd, TRIM_HEIGHT, (skirtEnd + hx) / 2, TRIM_HEIGHT / 2, z, dir, 0, 0, 1, 1));
    }
  }

  // The two cut ends. Where the skirting reaches the end of the wall the lower
  // band spans its full thickness, so the boards' returns close in the same
  // quad; where the boards stopped early they closed themselves, above.
  const skirted = skirtEnd >= hx - 0.01;
  for (const end of [1, -1] as const) {
    const dir: Facing = end > 0 ? 'px' : 'nx';
    const lower = end < 0 || skirted ? core + TRIM_PROUD * 2 : core;
    plain.push(face(core, height - TRIM_HEIGHT, end * hx, (height + TRIM_HEIGHT) / 2, 0, dir, 0, 0, 1, 1));
    plain.push(face(lower, TRIM_HEIGHT, end * hx, TRIM_HEIGHT / 2, 0, dir, 0, 0, 1, 1));
  }

  if (opts.capped) plain.push(face(len, core, 0, height, 0, 'py', 0, 0, 1, 1));

  return { paper, skirt, plain };
}

/** Append `b` onto `a` in place. Walls are usually built in more than one piece. */
export function addParts(a: WallParts, b: WallParts): WallParts {
  a.paper.push(...b.paper);
  a.skirt.push(...b.skirt);
  a.plain.push(...b.plain);
  return a;
}

/**
 * A square column of the building, floor to `height`, with the same skirting
 * around its foot that `meshbuilder.ts` puts around a structural pillar.
 */
export function buildingColumn(w: number, height: number, x: number, z: number, capped = false): WallParts {
  const paper: THREE.BufferGeometry[] = [];
  const skirt: THREE.BufferGeometry[] = [];
  const plain: THREE.BufferGeometry[] = [];
  const h = w / 2;
  const s = h + TRIM_PROUD;

  const sides: ReadonlyArray<readonly [Facing, number, number]> = [
    ['pz', 0, h],
    ['nz', 0, -h],
    ['px', h, 0],
    ['nx', -h, 0],
  ];
  for (const [dir, dx, dz] of sides) {
    paper.push(face(w, height - TRIM_HEIGHT, x + dx, (height + TRIM_HEIGHT) / 2, z + dz, dir, 0, TRIM_HEIGHT, w, height));
    const sx = dx === 0 ? 0 : Math.sign(dx) * s;
    const sz = dz === 0 ? 0 : Math.sign(dz) * s;
    skirt.push(face(w + TRIM_PROUD * 2, TRIM_HEIGHT, x + sx, TRIM_HEIGHT / 2, z + sz, dir, 0, 0, w + TRIM_PROUD * 2, 1));
  }
  skirt.push(face(w + TRIM_PROUD * 2, w + TRIM_PROUD * 2, x, TRIM_HEIGHT, z, 'py', 0, 0.94, 1, 1));
  if (capped) plain.push(face(w, w, x, height, z, 'py', 0, 0, 1, 1));

  return { paper, skirt, plain };
}
